import React from "react";
import { Avatar } from "antd";

// A small fixed palette so avatars stay on-brand instead of the arbitrary
// hues ui-avatars.com used to hand back. Background is picked deterministically
// from the name so the same person always gets the same color.
const PALETTE = [
  "#013BBE", // brand primary
  "#0f766e", // teal
  "#7c3aed", // violet
  "#b45309", // amber
  "#be185d", // pink
  "#1d4ed8", // blue
  "#047857", // emerald
  "#9333ea", // purple
];

function getInitials(name?: string | null): string {
  const trimmed = (name ?? "").trim();
  if (!trimmed) return "?";
  const parts = trimmed.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function colorForName(name?: string | null): string {
  const str = (name ?? "").trim() || "?";
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return PALETTE[Math.abs(hash) % PALETTE.length];
}

interface InitialsAvatarProps {
  name?: string | null;
  size?: number;
  className?: string;
  src?: string | null;
}

/**
 * Local replacement for the old `ui-avatars.com` remote image calls — no
 * network request, no third-party dependency. Renders the person/company's
 * initials in a deterministically-colored circle. Pass `src` to prefer a
 * real uploaded photo/logo when one exists (e.g. a field worker's profile
 * image); it falls back to initials when `src` is empty.
 */
const InitialsAvatar: React.FC<InitialsAvatarProps> = ({ name, size = 32, className, src }) => {
  if (src) {
    return (
      <Avatar
        src={src}
        size={size}
        className={className}
        alt={name ?? "avatar"}
      />
    );
  }

  return (
    <Avatar
      size={size}
      className={className}
      style={{
        backgroundColor: colorForName(name),
        color: "#fff",
        fontWeight: 600,
        fontSize: Math.max(10, Math.round(size * 0.4)),
      }}
    >
      {getInitials(name)}
    </Avatar>
  );
};

export default InitialsAvatar;
